import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import toast from 'react-hot-toast'
import { configApi, AdminConfig } from '../services/api'
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Save,
  X,
  CreditCard,
  Database,
  Cloud,
  Server,
  Banknote,
  Phone,
  Brain,
  Mail,
  Star,
  Target,
  Inbox,
  Smartphone
} from 'lucide-react'
import ReactJsonView from 'react-json-view'

const configSchema = z.object({
  key: z.string().min(1, 'Key is required'),
  value: z.string().min(1, 'Value is required'),
  description: z.string().optional(),
  encrypt: z.boolean().default(false),
})

type ConfigFormData = z.infer<typeof configSchema>

const ConfigurationPage: React.FC = () => {
  const queryClient = useQueryClient()
  const [selectedCategory, setSelectedCategory] = useState('stripe')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingConfig, setEditingConfig] = useState<AdminConfig | null>(null)
  const [showValues, setShowValues] = useState<Record<string, boolean>>({})

  const categories = [
    { id: 'stripe', name: 'Stripe', icon: CreditCard, description: 'SaaS subscription payments' },
    { id: 'helcim', name: 'Helcim', icon: CreditCard, description: 'Partner Connect (partner_config) for merchant payments' },
    { id: 'mailgun', name: 'Mailgun', icon: Mail, description: 'System emails (password reset, notifications)' },
    { id: 'plaid', name: 'Plaid', icon: Banknote, description: 'Banking and financial data' },
    { id: 'twilio', name: 'Twilio', icon: Phone, description: 'Voice, SMS, TaskRouter Contact Center & Web Chat' },
    { id: 'openai', name: 'OpenAI', icon: Brain, description: 'AI Chatbot & GPT services' },
    { id: 'redis', name: 'Redis', icon: Database, description: 'Cache and sessions' },
    { id: 'storage', name: 'Storage', icon: Cloud, description: 'Google Cloud Storage' },
    { id: 'database', name: 'Database', icon: Server, description: 'PostgreSQL settings' },
    { id: 'serviceflow_reviews', name: 'Product Reviews', icon: Star, description: 'ServiceFlow product review settings & milestones' },
    { id: 'google_ads', name: 'Google Ads', icon: Target, description: 'Google Ads & Local Services Ads lead integration' },
    { id: 'gmail', name: 'Gmail', icon: Inbox, description: 'Gmail OAuth for business email integration' },
    {
      id: 'mobile',
      name: 'Mobile',
      icon: Smartphone,
      description: 'Mobile app subscription UI (public read; no secrets here)',
    },
  ]

  const { data: configs, isLoading } = useQuery(
    ['configs', selectedCategory],
    () => configApi.getByCategory(selectedCategory),
    { enabled: !!selectedCategory }
  )

  const createMutation = useMutation(
    ({ category, key, data }: { category: string; key: string; data: any }) =>
      configApi.set(category, key, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['configs', selectedCategory])
        setShowCreateForm(false)
        toast.success('Configuration created successfully')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to create configuration')
      },
    }
  )

  const updateMutation = useMutation(
    ({ category, key, data }: { category: string; key: string; data: any }) =>
      configApi.set(category, key, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['configs', selectedCategory])
        setEditingConfig(null)
        toast.success('Configuration updated successfully')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to update configuration')
      },
    }
  )

  const deleteMutation = useMutation(
    ({ category, key }: { category: string; key: string }) =>
      configApi.delete(category, key),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['configs', selectedCategory])
        toast.success('Configuration deleted successfully')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to delete configuration')
      },
    }
  )

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ConfigFormData>({
    resolver: zodResolver(configSchema),
  })

  const onSubmit = (data: ConfigFormData) => {
    try {
      const value = JSON.parse(data.value)
      const configData = {
        value,
        encrypt: data.encrypt,
        description: data.description,
      }

      if (editingConfig) {
        updateMutation.mutate({
          category: selectedCategory,
          key: data.key,
          data: configData,
        })
      } else {
        createMutation.mutate({
          category: selectedCategory,
          key: data.key,
          data: configData,
        })
      }
    } catch (error) {
      toast.error('Invalid JSON format in value field')
    }
  }

  const handleEdit = (config: AdminConfig) => {
    setEditingConfig(config)
    reset({
      key: config.key,
      value: JSON.stringify(config.value, null, 2),
      description: config.description || '',
      encrypt: config.isEncrypted,
    })
    setShowCreateForm(true)
  }

  const handleDelete = (config: AdminConfig) => {
    if (window.confirm(`Are you sure you want to delete the configuration "${config.key}"?`)) {
      deleteMutation.mutate({
        category: selectedCategory,
        key: config.key,
      })
    }
  }

  const toggleShowValue = (configId: string) => {
    setShowValues(prev => ({
      ...prev,
      [configId]: !prev[configId]
    }))
  }

  const handleCancelForm = () => {
    setShowCreateForm(false)
    setEditingConfig(null)
    reset()
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configuration Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage system-wide configuration settings
          </p>
        </div>
        <button
          onClick={() => {
            setEditingConfig(null)
            reset({
              key: '',
              value: '{\n  \n}',
              description: '',
              encrypt: false
            })
            setShowCreateForm(true)
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Configuration
        </button>
      </div>

      {/* Category Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {categories.map((category) => {
            const Icon = category.icon
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                  selectedCategory === category.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {category.name}
              </button>
            )
          })}
        </nav>
      </div>

      {selectedCategory === 'mobile' && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <p className="font-medium">Mobile subscription UI</p>
          <p className="mt-1 text-blue-800">
            Add configuration key <code className="rounded bg-blue-100 px-1">subscription_ui</code> with JSON
            value, for example:
          </p>
          <pre className="mt-2 overflow-x-auto rounded bg-white p-2 text-xs text-gray-800">
            {`{\n  "stripeHostedCheckoutEnabled": true,\n  "prioritizeStripeCheckout": false,\n  "useStripePaymentSheet": true,\n  "useStripeHostedCheckoutPage": false\n}`}
          </pre>
          <p className="mt-2 text-blue-800">
            When <code className="rounded bg-blue-100 px-1">stripeHostedCheckoutEnabled</code> is true: if{' '}
            <code className="rounded bg-blue-100 px-1">prioritizeStripeCheckout</code> is true, the mobile UI
            offers only Stripe (in-app Payment Sheet when{' '}
            <code className="rounded bg-blue-100 px-1">useStripePaymentSheet</code> is true; otherwise a browser
            checkout URL). When <code className="rounded bg-blue-100 px-1">prioritizeStripeCheckout</code> is false,
            the mobile UI offers only in-app purchases. When the hosted-enable flag is off, subscription entry screens
            and the plan modal use in-app purchases only (no browser “upgrade” button). Payment Sheet requires SaaS
            Stripe keys in Admin → Stripe →{' '}
            <code className="rounded bg-blue-100 px-1">saas_config</code> (including{' '}
            <code className="rounded bg-blue-100 px-1">publishableKey</code>). Set{' '}
            <code className="rounded bg-blue-100 px-1">useStripeHostedCheckoutPage</code> or{' '}
            <code className="rounded bg-blue-100 px-1">forceStripeHostedPage</code> to true to force browser/SafariView
            checkout instead of Payment Sheet. The same public mobile UI response also includes{' '}
            <code className="rounded bg-blue-100 px-1">saasPublishableKey</code> (from{' '}
            <code className="rounded bg-blue-100 px-1">saas_config</code>) so the app can initialize Stripe before a
            tenant connects their own Stripe account.
          </p>
        </div>
      )}

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              {editingConfig ? 'Edit Configuration' : 'Create New Configuration'}
            </h3>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label htmlFor="key" className="block text-sm font-medium text-gray-700">
                  Configuration Key
                </label>
                <input
                  {...register('key')}
                  type="text"
                  disabled={!!editingConfig}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-gray-100"
                  placeholder="e.g., saas_config"
                />
                {errors.key && (
                  <p className="mt-1 text-sm text-red-600">{errors.key.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="value" className="block text-sm font-medium text-gray-700">
                  Configuration Value (JSON)
                </label>
                <textarea
                  {...register('value')}
                  rows={8}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm font-mono"
                  placeholder='{"key": "value"}'
                />
                {errors.value && (
                  <p className="mt-1 text-sm text-red-600">{errors.value.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <input
                  {...register('description')}
                  type="text"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Brief description of this configuration"
                />
              </div>

              <div className="flex items-center">
                <input
                  {...register('encrypt')}
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="encrypt" className="ml-2 block text-sm text-gray-900">
                  Encrypt this configuration (recommended for sensitive data)
                </label>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancelForm}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {editingConfig ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Configurations List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            {categories.find(c => c.id === selectedCategory)?.name} Configurations
          </h3>
          
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : configs && configs.length > 0 ? (
            <div className="space-y-4">
              {configs.map((config) => (
                <div key={config.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h4 className="text-sm font-medium text-gray-900">{config.key}</h4>
                        {config.isEncrypted && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Encrypted
                          </span>
                        )}
                      </div>
                      {config.description && (
                        <p className="text-sm text-gray-500 mt-1">{config.description}</p>
                      )}
                      <div className="mt-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">Value:</span>
                          <button
                            onClick={() => toggleShowValue(config.id)}
                            className="text-xs text-primary-600 hover:text-primary-800 flex items-center"
                          >
                            {showValues[config.id] ? (
                              <>
                                <EyeOff className="h-3 w-3 mr-1" />
                                Hide
                              </>
                            ) : (
                              <>
                                <Eye className="h-3 w-3 mr-1" />
                                Show
                              </>
                            )}
                          </button>
                        </div>
                        {showValues[config.id] && (
                          <div className="mt-2 p-3 bg-gray-50 rounded-md">
                            <ReactJsonView
                              src={config.value}
                              theme="rjv-default"
                              displayDataTypes={false}
                              displayObjectSize={false}
                              enableClipboard={false}
                              collapsed={1}
                            />
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 mt-2">
                        Updated: {new Date(config.updatedAt).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => handleEdit(config)}
                        className="text-primary-600 hover:text-primary-800"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(config)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">No configurations found for this category</p>
              <button
                onClick={() => setShowCreateForm(true)}
                className="mt-2 text-primary-600 hover:text-primary-800 text-sm font-medium"
              >
                Create your first configuration
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ConfigurationPage
